import styles from './Dashboard.module.css';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';

import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';

import React from 'react';

import { DateTime } from 'luxon';

import { applyRegistrationNotOpen } from "../../registrationNotOpen.js";
import { getPatroller, setDutyAssignment, clearDutyAssignment, clockInOut } from "../../api.js";

import { checkClockIn, checkClockOut, clockErrorMessage } from "../../utils/clockWindow.js";

import { useAuth } from "../../AuthContext";
import { useFCM } from '../../hooks/useFCM.jsx';
import { HillCaptain } from '../HillCaptain/HillCaptain';
import { WeatherDisplay } from '../Weather/WeatherDisplay';
import { Patroller } from '../Patroller/Patroller';
import { Duties } from '../Duties/Duties';
import { Shifts } from '../Shifts/Shifts';
import { Radios } from '../Radios/Radios';

const ConfirmDialog = ({ open, title, message, onConfirm, onCancel }) => (
    <Dialog open={ Boolean(open) } onClose={ onCancel }>
        { title && <h3 style={ { margin: 16 } }>{ title }</h3> }

        <DialogContent>{ message }</DialogContent>

        <Divider variant='middle' />

        <DialogActions sx={{ mt: 2 }}>
            <Button variant="outlined" color='secondary' onClick={ onCancel } startIcon={ <Cancel /> }>Cancel</Button>
            <Button variant="contained" color='primary' onClick={ onConfirm } endIcon={ <CheckCircle /> }>OK</Button>
        </DialogActions>
    </Dialog>
);

const FIFTEEN_MINUTES = 15 * 60 * 1000;

export const Dashboard = () => {
    const [ error, setError ] = React.useState(null);
    const [ fcmMessage, setFcmMessage ] = React.useState(null);
    const [ isHillCaptain, setIsHillCaptain ] = React.useState(false);
    const [ patroller, setPatroller ] = React.useState(null);
    const [ activeShift, setActiveShift ] = React.useState(null);
    const [ showConfirmDialog, setShowConfirmDialog ] = React.useState(false);
    const [ confirmClock, setConfirmClock ] = React.useState(false);
    const [ confirmClockTitle, setConfirmClockTitle ] = React.useState('');
    const [ confirmClockMessage, setConfirmClockMessage ] = React.useState('');
    const [ pendingClock, setPendingClock ] = React.useState(null);

    const { auth, token } = useAuth(); 

    const handleOnMessage = React.useCallback((payload) => {
        setFcmMessage(payload);
    }, []);

    const handleSetPatroller = React.useCallback((patrollerData) => {
        setIsHillCaptain(patrollerData.certifications?.some(c => c.name.toLowerCase() === 'hill captain'));
        setPatroller(patrollerData);
    }, []);

    useFCM(handleOnMessage);

    const handleAssignDuty = async (shift, assigned, role) => {
        const success = assigned ? await clearDutyAssignment(patroller.id, shift.id, role.id, token) : await setDutyAssignment(patroller.id, shift.id, role.id, token);

        // Server says registration is not open yet: show the Registration Not Open alert, no refetch, no retry.
        if (success?.error === 'manage/registration-not-open') {
            setPatroller(prev => applyRegistrationNotOpen(prev, shift.event?.id, success.opensAt));

            return;
        }

        if (success) {
            setPatroller(await getPatroller(auth.currentUser.uid, token));
        }

        if (success.error) {
            if (success.error === 'role-unavailable') {
                setError('The selected role is no longer available for this shift.');
            }
        }
    };

    const handleClockChange = async (shift) => {
        const now = DateTime.now().toMillis();
        const timelog = shift.timelogs?.find(t => t.patroller === auth.currentUser.uid);
        const started = timelog?.started;
        const stopped = timelog?.stopped;

        setError(null);

        // Window rules are checked here too so an out-of-window request is never sent.
        if (!started && !stopped) {
            const check = checkClockIn(shift, now);

            if (!check.ok) {
                setError(clockErrorMessage({ status: 409, error: 'manage/shift-window-closed', reason: check.reason }));

                return;
            }
        } else if (started && !stopped) {
            const check = checkClockOut(shift, now);

            if (!check.ok) {
                setError(clockErrorMessage({ status: 409, error: 'manage/shift-window-closed', reason: check.reason }));

                return;
            }

            // ask user to confirm early clock-out if more than 15 minutes before shift end
            if ((shift.end - now) > FIFTEEN_MINUTES && !confirmClock) {
                setConfirmClockTitle('Confirm Early Clock-Out');
                setConfirmClockMessage('You are about to clock out more than 15 minutes before your shift ends. Are you sure you want to proceed?');
                setPendingClock({ shiftId: shift.id, started, stopped });
                setShowConfirmDialog(true);

                return;
            }
        }

        await shiftClockInOut({ shiftId: shift.id, started, stopped });
    };

    const handleActiveShiftChange = (shift) => {
        setActiveShift(shift);
    };

    const handleConfirmDialog = () => {
        setConfirmClock(true);
        setShowConfirmDialog(false);

        if (pendingClock) {
            shiftClockInOut(pendingClock);
        }
    };

    const handleCancelDialog = () => {
        setConfirmClock(false);
        setShowConfirmDialog(false);
        setPendingClock(null);
    };

    const shiftClockInOut = async ({ shiftId, started, stopped }) => {
        if (!shiftId || stopped) return;

        // One request, no retry, nothing written automatically for missed shifts.
        const result = await clockInOut(auth.currentUser.uid, shiftId, started ? 'stopped' : 'started', token);

        if (!result?.ok) {
            setError(clockErrorMessage(result));
        }

        setPendingClock(null);
        setConfirmClock(false);

        // Exactly one refetch to resync with the server.
        setPatroller(await getPatroller(auth.currentUser.uid, token));
    };

    return (
        <div className={ styles.dashboard }>
            { error && <Alert variant='filled' severity="warning">{ error }</Alert> }

            { fcmMessage && <Alert variant='filled' severity="info">{ fcmMessage.notification?.title }: { fcmMessage.notification?.body }</Alert> }

            <Patroller id={ auth.currentUser.uid } onSetPatroller={ handleSetPatroller } />

            { patroller?.id && (
                <>
                    <Duties patroller={ patroller } onOffDuty={ handleClockChange } onAssignDuty={ handleAssignDuty } onActiveShiftChange={ handleActiveShiftChange } />

                    <WeatherDisplay />

                    <HillCaptain user={ patroller } currentShift={ activeShift } />

                    <Divider variant="middle" color="primary" sx={ { marginTop: 3, marginBottom: 3, bgcolor: "warning.light" } } flexItem />

                    <Shifts patroller={ patroller } onAssignDuty={ handleAssignDuty } />

                    <Radios hillCaptain={ isHillCaptain } />
                </>
            ) }

            <ConfirmDialog open={ showConfirmDialog } title={ confirmClockTitle } message={ confirmClockMessage } onConfirm={ handleConfirmDialog } onCancel={ handleCancelDialog } />
        </div>
    );
};